import "./App.css";
import React, { useEffect, useMemo, useRef } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import RootLayout from "./layouts/root.loyaut";
import { createTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import theme from "./theme";
import LoginPage from "./features/auth/pages/LoginPage";
import RegisterPage from "./features/auth/pages/RegisterPage";
import ForgotPasswordPage from "./features/auth/pages/ForgotPasswordPage";
import EmailActivationPage from "./features/auth/pages/EmailActivationPage";
import HomePage from "./components/Main/Home/home-page"; // legacy logic: original HomePage component
import FeedPage from "./features/feed/FeedPage";
import { ToastContainer } from "react-toastify";
import ChooseUserType from "./choose-user-type/choose-user-type";
import Refresh from "./effects/Refresh";
import { ProfilePage, USER_TYPE_IDS } from "./components/Header/ProfileViewer/profile-viewer";
import ProfileRedirect from "./components/ProfileRedirect";
import ResetPassword from "./features/auth/pages/ResetPassword";
import PostCreator from "./components/Main/Sidebar/PostCreator/post-creator";
import CheckAccess from "./effects/CheckAccess";
import NotFound from "./components/Client/Other/NotFound";
import PaymentFailedPage from "./components/Client/Other/PaymentFailedPage";
import Settings from "./components/Header/settings/settings";
import ComplaintsPolicyPage from "./components/Footer/Pages/ComplaintsPolicy";
import TheVaultPage from "./components/Footer/Pages/TheVaultPage";
import FineArtGlamourPage from "./components/Footer/Pages/FineArtGlamourPage";
import VirtualCompanionshipPage from "./components/Footer/Pages/VirtualCompanionshipPage";
import AboutUsPage from "./components/Footer/Pages/AboutUsPage";
import LifestyleTravelPage from "./pages/LifestyleTravelPage";
import TermsOfServicePage from "./components/Footer/Pages/TermsOfService";
import ContentProviderAgreement from "./components/Main/Sidebar/PostCreator/ContentProviderAgreement";
import LikedPosts from "./components/Main/Sidebar/Favorites/postsWithLike";
import Achievements from "./components/Main/Sidebar/Achievements/achievements";
import MessagesPage from "./components/Main/Sidebar/Messages/messages";
import SubscriptionsPage from "./components/Main/Sidebar/Subscriptions/subscriptions";
import ReferralProgram from "./components/Main/Sidebar/ReferralProgram/referral-program";
import AdminDashboard from "./components/Main/Sidebar/AdminPanel/Admin/AdminDashboard";
import GuideStepsRedirect from "./components/elements/GuideStepsRedirect";
import FlaggedContentPage from "./components/Main/Sidebar/AdminPanel/Admin/FlaggedContent";
import AdminTransactions from "./components/Main/Sidebar/AdminPanel/Admin/AdminTransactions";
import ComplianceStatement from "./components/Footer/Pages/ComplianceStatement";
import KYCAMLCompliancePolicy from "./components/Footer/Pages/KYCAMLCompliancePolicy";
import CookiePolicy from "./components/Footer/Pages/CookiePolicy";
import Sitemap from "./components/Sitemap";
import { UserStatusProvider } from "./utils/UserStatusContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import SocketInit from "./utils/SocketInit";
import RefundPolicy from "./components/Footer/Pages/RefundPolicy";
import ComingSoonPage from "./pages/ComingSoonPage";
import GenesisCollectionPage from "./pages/GenesisCollectionPage";
import SubscriptionPlansPage from "./pages/SubscriptionPlansPage/SubscriptionPlansPage";
import AcceptableUsePolicy from "./components/Footer/Pages/AcceptableUsePolicy";
import DMCATakedownPolicy from "./components/Footer/Pages/DMCATakedownPolicy";
import PrivacyPolicy from "./components/Footer/Pages/PrivacyPolicy";
import AdminAnalyticsPage from "./components/Main/Sidebar/Achievements/AdminDashboard/adminDashboard";
import PayoutsPage from "./components/Header/settings/creator/PayoutsPage";
import AdminPayoutsPage from "./components/Main/Sidebar/payounts/admin/AdminPayoutsPage";
import AdminTaxFormsPage from "./components/Main/Sidebar/payounts/form/AdminTaxFormsPage";
import { Helmet } from "react-helmet";
import useAgeApproveSocket from "./hooks/use-age-approve-socket";
import RecaptchaProvider from "./components/RecaptchaProvider";
import AgeVerificationGate from "./components/AgeVerification/AgeVerificationGate";
import { useGlobalUserInfo } from "./hooks/useGlobalUserInfo";
import { useGlobalMonetization } from "./hooks/useGlobalMonetization";
import DeletedPostModalManager from "./components/elements/DeletedPostModalManager";
import CreateFirstPostModalManager from "./components/elements/CreateFirstPostModalManager";
import { GoogleOAuthProvider } from "@react-oauth/google";
import BannedUserModalManager from "./components/elements/Banned/BannedUserPageManager";
import BannedUserRedirect from "./components/elements/BannedUserRedirect";
import MemberOnboardingGuard from "./components/elements/MemberOnboardingGuard";
import CreatorOnboardingGuard from "./components/elements/CreatorOnboardingGuard";
import { useAppSelector } from "./redux/typedHooks";
import RouteAnalytics from "./analytics/RouteAnalytics";
import { isSearchIndexingBlocked } from "./config/searchIndexing";
import { buildCanonicalUrl } from "./seo/canonicalUrl";
import AdminUpdateCenter from "./components/Main/Sidebar/AdminPanel/Admin/AdminUpdateCenter";
import GeneralLandingPage from "./components/Main/Landing/GeneralLandingPage";
import CreatorsLandingPage from "./components/Main/Landing/CreatorsLandingPage";
import CreatorApplication from "./components/Main/CreatorApplication/CreatorApplication";
import MemberOnboarding from "./components/Main/MemberOnboarding/MemberOnboarding";
import NewsletterPage from "./features/public/pages/NewsletterPage";

// DIDIT INTEGRATION - COMMENTED OUT
// import VerificationSuccess from "./components/VerificationSuccess";
// import AutoVerificationPage from "./components/AutoVerificationPage";
// import VerificationTest from "./components/elements/VerificationTest";

const DEFAULT_SEO = {
  title: "Sugar Cribs | Creator Platform",
  description:
    "Discover creators unlock exclusive content and connect on Sugar Cribs.",
};

/**
 * Legacy fallback SEO map for routes not yet covered by a page-level
 * `StandardPageHelmet` + `resolvePageSeo` integration.
 *
 * TODO: As each route adopts `StandardPageHelmet`, remove its entry from
 * here so there is a single source of truth. Do NOT update title/description
 * here for routes that already call `resolvePageSeo` — those pages own their
 * own SEO and this map will never be reached for them.
 */
const SEO_BY_PATH = {
  "/": {
    title: "Home | Sugar Cribs",
    description:
      "Explore creators and discover trending content on Sugar Cribs.",
  },
  "/discover": {
    title: "Discover | Sugar Cribs",
    description:
      "Discover exclusive content and creators on Sugar Cribs.",
  },
  "/about-us": {
    title: "About Us | Sugar Cribs",
    description:
      "Learn more about Sugar Cribs, our vision, and curated community.",
  },
  "/login": {
    title: "Login | Sugar Cribs",
    description: "Sign in to your Sugar Cribs account.",
  },
  "/register": {
    title: "Sign Up | Sugar Cribs",
    description: "Create your Sugar Cribs account and start exploring creators.",
  },
  "/forgot-password": {
    title: "Forgot Password | Sugar Cribs",
    description: "Reset your Sugar Cribs password and regain account access.",
  },
  "/reset-password": {
    title: "Reset Password | Sugar Cribs",
    description: "Set a new password for your Sugar Cribs account.",
  },
  "/choose-user-type": {
    title: "Onboarding | Sugar Cribs",
    description: "Complete onboarding and set up your Sugar Cribs profile.",
  },
  "/email-activation": {
    title: "Email Activation | Sugar Cribs",
    description: "Activate your email to continue using Sugar Cribs.",
  },
  "/post-creator": {
    title: "Post Creator | Sugar Cribs",
    description: "Create and publish new content on Sugar Cribs.",
  },
  "/liked-posts": {
    title: "Liked Posts | Sugar Cribs",
    description: "Review posts you liked and revisit favorite creator content.",
  },
  "/referral": {
    title: "Referral Program | Sugar Cribs",
    description: "Invite friends and earn rewards in the Sugar Cribs referral program.",
  },
  "/messages": {
    title: "Messages | Sugar Cribs",
    description: "Chat with creators and manage conversations on Sugar Cribs.",
  },
  "/subscriptions": {
    title: "Subscriptions | Sugar Cribs",
    description: "Manage subscriptions and unlock creator content on Sugar Cribs.",
  },
  "/dashboard": {
    title: "Dashboard | Sugar Cribs",
    description: "Track your creator performance and account insights.",
  },
  "/adminDashboard": {
    title: "Admin Dashboard | Sugar Cribs",
    description: "Monitor platform analytics and admin metrics.",
  },
  "/creator-payouts": {
    title: "Creator Payouts | Sugar Cribs",
    description: "Review payouts and earnings as a creator.",
  },
  "/admin-payouts": {
    title: "Admin Payouts | Sugar Cribs",
    description: "Manage and review payout operations as admin.",
  },
  "/admin/tax-forms": {
    title: "Admin Tax Forms | Sugar Cribs",
    description: "Review and manage creator tax forms.",
  },
  "/settings": {
    title: "Settings | Sugar Cribs",
    description: "Manage account, billing, privacy, and notification settings.",
  },
  "/admin/age-proofs": {
    title: "Age Proofs | Sugar Cribs",
    description: "Review user age proof submissions in admin panel.",
  },
  "/admin/moderation": {
    title: "Moderation | Sugar Cribs",
    description: "Review flagged content and moderation tasks.",
  },
  "/admin/transactions": {
    title: "Admin Transactions | Sugar Cribs",
    description: "Inspect platform transactions and payment events.",
  },
  "/admin/update-center": {
    title: "Admin Update Center | Sugar Cribs",
    description: "Manage platform updates and maintenance tasks.",
  },
  "/complaints-policy": {
    title: "Complaints Policy | Sugar Cribs",
    description: "Read the Sugar Cribs complaints policy.",
  },
  "/terms-of-service": {
    title: "Terms of Service | Sugar Cribs",
    description: "Read the Sugar Cribs terms of service.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Sugar Cribs",
    description: "Read how Sugar Cribs handles and protects personal data.",
  },
  "/refund-policy": {
    title: "Refund Policy | Sugar Cribs",
    description: "Review Sugar Cribs refund terms and eligibility.",
  },
  "/acceptable-use-policy": {
    title: "Acceptable Use Policy | Sugar Cribs",
    description: "Read the acceptable use rules for Sugar Cribs.",
  },
  "/DMCA-takedown-policy": {
    title: "DMCA Takedown Policy | Sugar Cribs",
    description: "Read DMCA takedown and copyright process details.",
  },
  "/compliance-statement": {
    title: "Compliance Statement | Sugar Cribs",
    description: "Review Sugar Cribs legal and compliance statement.",
  },
  "/kyc-aml-compliance-policy": {
    title: "KYC/AML Compliance Policy | Sugar Cribs",
    description: "Read KYC and AML compliance rules for Sugar Cribs.",
  },
  "/cookie-policy": {
    title: "Cookie Policy | Sugar Cribs",
    description: "Learn how Sugar Cribs uses cookies and tracking technologies.",
  },
  "/content-provider-agreement": {
    title: "Content Provider Agreement | Sugar Cribs",
    description: "Review terms for creators publishing content on Sugar Cribs.",
  },
  "/user-banned": {
    title: "Account Restricted | Sugar Cribs",
    description: "This account is currently restricted on Sugar Cribs.",
  },
  "/payment-failed": {
    title: "Payment failed | Sugar Cribs",
    description:
      "Your payment could not be completed. You can return home or contact support.",
  },
  "/virtual-companionship": {
    title: "Virtual Companionship | Sugar Cribs",
    description: "Connect with verified creators for authentic virtual companionship and exclusive 1-on-1 experiences.",
  },
  "/fine-art-glamour": {
    title: "Fine Art & Glamour | Sugar Cribs",
    description: "Explore curated fine art glamour, exclusive portfolios, and masterwork collections on Sugar Cribs.",
  },
  "/sitemap.xml": {
    title: "Sitemap | Sugar Cribs",
    description: "Sitemap and indexing structure for Sugar Cribs pages.",
  },
  "/subscription-plans": {
    title: "Subscription Plans | Sugar Cribs",
    description: "Choose a subscription membership plan and get access to exclusive creator content.",
  },
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}

function RouteSeoFallback() {
  const location = useLocation();
  const pathname = location.pathname;

  const canonical = useMemo(
    () =>
      buildCanonicalUrl(
        typeof window !== "undefined" ? window.location.href : pathname,
        { dropTrackingParams: true }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  const isProfileRoute =
    pathname.startsWith("/") &&
    pathname.split("/").filter(Boolean).length === 1 &&
    !Object.prototype.hasOwnProperty.call(SEO_BY_PATH, pathname);

  const isNotFoundRoute = !Object.prototype.hasOwnProperty.call(
    SEO_BY_PATH,
    pathname
  ) && !isProfileRoute;

  const fallbackSeo = isProfileRoute
    ? {
      title: "Creator Profile | Sugar Cribs",
      description:
        "View creator profile details, content, and subscription options on Sugar Cribs.",
    }
    : isNotFoundRoute
      ? {
        title: "Page Not Found | Sugar Cribs",
        description:
          "The page you are looking for does not exist on Sugar Cribs.",
      }
      : SEO_BY_PATH[pathname] || DEFAULT_SEO;

  return (
    <Helmet>
      <title>{fallbackSeo.title}</title>
      <meta name="description" content={fallbackSeo.description} />
      <link rel="canonical" href={canonical} />
    </Helmet>
  );
}

const darkTheme = createTheme({
  ...theme,
  palette: {
    mode: "dark",
    primary: {
      main: "#f06ec4",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function OnboardingGuard() {
  const location = useLocation();
  const reduxUser = useAppSelector((state) => state.user.currentUser);

  const user = (() => {
    try {
      const stored =
        typeof localStorage !== "undefined"
          ? localStorage.getItem("user")
          : null;
      return stored ? JSON.parse(stored) : reduxUser;
    } catch {
      return reduxUser;
    }
  })();

  const rawUserType = user?.userType || user?.usertype || user?.user_type;
  const userTypeTitle = (typeof rawUserType === "string" ? rawUserType : rawUserType?.title)?.toLowerCase();
  const userTypeId = typeof rawUserType === "object" ? (rawUserType?._id || rawUserType?.id) : rawUserType;

  const isCreator =
    location.pathname.startsWith("/apply-as-creator") ||
    userTypeTitle === "creator" ||
    userTypeTitle === "model" ||
    userTypeId === USER_TYPE_IDS?.CREATOR ||
    userTypeId === "6800a63485ec077c7c266c25" ||
    userTypeTitle === "6800a63485ec077c7c266c25" ||
    user?.contentProviderAgreementAccepted === true;

  return isCreator ? <CreatorOnboardingGuard /> : <MemberOnboardingGuard />;
}

function App({ RouterComponent = Router, routerProps = {} }) {
  useAgeApproveSocket();
  const userData = useGlobalUserInfo();
  const hasAuthToken =
    typeof window !== "undefined" &&
    Boolean(
      localStorage.getItem("token") || localStorage.getItem("access_token")
    );
  const isUserAuthenticated = Boolean(
    hasAuthToken &&
      (userData ||
        (typeof localStorage !== "undefined" && localStorage.getItem("user")))
  );
  const { hasPremiumSubscription } = useGlobalMonetization();
  const hasPremiumRef = useRef(hasPremiumSubscription);
  hasPremiumRef.current = hasPremiumSubscription;

  useEffect(() => {
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      if (hasPremiumRef.current) return;
      if (event.target && (event.target.nodeName === "IMG" || event.target.nodeName === "VIDEO")) {
        event.preventDefault();
      }
    };
    document.addEventListener("contextmenu", handleContextMenu, false);
    return () => document.removeEventListener("contextmenu", handleContextMenu, false);
  }, []);

  useEffect(() => {
    const styleId = "premium-content-pointer-events";
    let el = document.getElementById(styleId);
    if (!hasPremiumSubscription) {
      if (!el) {
        el = document.createElement("style");
        el.id = styleId;
        el.textContent = "img { pointer-events: none !important; }";
        document.head.appendChild(el);
      }
    } else {
      if (el) {
        el.remove();
      }
    }
    return () => {
      const node = document.getElementById(styleId);
      if (node) node.remove();
    };
  }, [hasPremiumSubscription]);

  return (
    <>
      <Helmet>
        <meta
          name="robots"
          content={
            isSearchIndexingBlocked() ? "noindex,nofollow" : "index,follow"
          }
        />
        <meta name="rating" content="RTA-5042-1996-1400-1577-RTA" />
        <meta name="ASACP-ORG" content="http://www.asacp.org/" />
      </Helmet>

      <ToastContainer />
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
        <CookieConsentProvider>
          <UserStatusProvider>
            <SocketInit />
            <ThemeProvider theme={darkTheme}>
              <RouterComponent {...routerProps}>
                <ScrollToTop />
                <RouteSeoFallback />
                <RouteAnalytics />
                <BannedUserRedirect />
                <GuideStepsRedirect />
                <OnboardingGuard />
                <RecaptchaProvider>
                  <Switch>
                    <Route exact path="/">
                      {isUserAuthenticated ? (
                        // <Refresh>
                        //   <CheckAccess>
                        //     <FeedPage />
                        //   </CheckAccess>
                        // </Refresh>
                        <RootLayout>
                          <Refresh>
                            <CheckAccess>
                              <HomePage />
                            </CheckAccess>
                          </Refresh>
                        </RootLayout>
                      ) : (
                        <GeneralLandingPage />
                      )}
                    </Route>
                    <Route exact path="/feed-under-dev">
                      <FeedPage />
                    </Route>
                    <Route exact path="/creators">
                      <CreatorsLandingPage />
                    </Route>
                    <Route exact path="/apply-as-creator">
                      <CreatorApplication />
                    </Route>
                    <Route exact path="/member-onboarding-step">
                      <MemberOnboarding />
                    </Route>
                    <Route exact path="/newsletter">
                      <NewsletterPage />
                    </Route>
                    <Route exact path="/homepage">
                      <GeneralLandingPage />
                    </Route>
                    <Route exact path="/the-vault" component={TheVaultPage} />
                    <Route exact path="/choose-user-type">
                      <ChooseUserType />
                    </Route>
                    <Route exact path="/login">
                      <AgeVerificationGate>
                        <LoginPage />
                      </AgeVerificationGate>
                    </Route>
                    <Route
                      exact
                      path="/forgot-password"
                      component={ForgotPasswordPage}
                    />
                    <Route path="/reset-password" component={ResetPassword} />
                    <Route exact path="/register">
                      <AgeVerificationGate>
                        <RegisterPage />
                      </AgeVerificationGate>
                    </Route>
                    <Route exact path="/email-activation" component={EmailActivationPage} />
                    <Route exact path="/post-creator">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <PostCreator />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/liked-posts">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <LikedPosts visionType="Liked Posts" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/bookmarks">
                      <Redirect to="/liked-posts?tab=saved" />
                    </Route>
                    <Route exact path="/referral">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <ReferralProgram />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>

                    <Route exact path="/messages">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <MessagesPage visionType="Messages" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/subscriptions">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <SubscriptionsPage />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/dashboard">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <Achievements visionType="Shared Posts" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/adminDashboard">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminAnalyticsPage visionType="adminDashboard" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/creator-payouts">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <PayoutsPage visionType="adminDashboard" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin-payouts">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminPayoutsPage visionType="adminDashboard" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin/tax-forms">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminTaxFormsPage visionType="adminDashboard" />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/settings">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <Settings />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin/age-proofs">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminDashboard />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin/moderation">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <FlaggedContentPage />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin/transactions">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminTransactions />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/admin/update-center">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <AdminUpdateCenter />
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route exact path="/complaints-policy">
                      <RootLayout>
                        <ComplaintsPolicyPage />
                      </RootLayout>
                    </Route>
                    <Route exact path="/terms-of-service">
                      <RootLayout>
                        <TermsOfServicePage />
                      </RootLayout>
                    </Route>
                    <Route exact path="/privacy-policy">
                      <RootLayout>
                        <PrivacyPolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/refund-policy">
                      <RootLayout>
                        <RefundPolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/acceptable-use-policy">
                      <RootLayout>
                        <AcceptableUsePolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/DMCA-takedown-policy">
                      <RootLayout>
                        <DMCATakedownPolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/compliance-statement">
                      <RootLayout>
                        <ComplianceStatement />
                      </RootLayout>
                    </Route>
                    <Route exact path="/kyc-aml-compliance-policy">
                      <RootLayout>
                        <KYCAMLCompliancePolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/cookie-policy">
                      <RootLayout>
                        <CookiePolicy />
                      </RootLayout>
                    </Route>
                    <Route exact path="/content-provider-agreement">
                      <RootLayout>
                        <ContentProviderAgreement />
                      </RootLayout>
                    </Route>
                    <Route exact path="/user-banned">
                      <BannedUserModalManager />
                    </Route>
                    <Route exact path="/payment-failed">
                      <PaymentFailedPage />
                    </Route>
                    <Route exact path="/virtual-companionship" component={VirtualCompanionshipPage} />
                    <Route exact path="/discover">
                      <ComingSoonPage pageName="Discover" />
                    </Route>
                    <Route exact path="/lifestyle-and-luxury-travel" component={LifestyleTravelPage} />
                    <Route exact path="/fine-art-glamour" component={FineArtGlamourPage} />
                    <Route exact path="/about-us" component={AboutUsPage} />
                    <Route exact path="/genesis-collection">
                      <GenesisCollectionPage />
                    </Route>
                    <Route exact path="/subscription-plans">
                      <SubscriptionPlansPage />
                    </Route>
                    <Route exact path="/sitemap.xml">
                      <Sitemap />
                    </Route>
                    <Route exact path="/:id">
                      <RootLayout>
                        <Refresh>
                          <CheckAccess>
                            <ProfileRedirect>
                              <ProfilePage />
                            </ProfileRedirect>
                          </CheckAccess>
                        </Refresh>
                      </RootLayout>
                    </Route>
                    <Route>
                      <NotFound />
                    </Route>
                  </Switch>
                </RecaptchaProvider>
              </RouterComponent>
              <CookieConsent />
              <DeletedPostModalManager />
              <CreateFirstPostModalManager />
            </ThemeProvider>
          </UserStatusProvider>
        </CookieConsentProvider>
      </GoogleOAuthProvider >
    </>
  );
}

// function PrivateRoute({ component: Component, ...rest }) {
//   return (
//     <Route
//       {...rest}
//       render={(props) =>
//         localStorage.getItem("token") ? (
//           <Component {...props} />
//         ) : (
//           <Redirect
//             to={{
//               pathname: "/",
//               state: {
//                 from: props.location,
//               },
//             }}
//           />
//         )
//       }
//     />
//   );
// }

export default App;
